// ============================================
// useVerdictSubmit — UKA-X01 D6
//
// Handles verdict submission for a single ClinicalReviewItem.
// Writes an immutable VerdictRecord to:
//   /tenants/{tenantId}/content/{contentId}/verdicts/{verdictId}
//
// Includes:
//   - SHA-256 content hash of body at verdict time (TOCTOU forensic chain)
//   - Session ID from D2 auth context
//   - User agent for device fingerprinting
//   - Server timestamp (never client-supplied)
//   - Optimistic UI update: success state set before Firestore confirm
// ============================================

import { useCallback, useState } from 'react';
import {
  collection,
  doc,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getSessionId, getSessionIpAddress, isAuthenticated } from '../lib/auth';
import { enqueue } from '../lib/offlineQueue';
import type { ClinicalReviewItem, VerdictAction, ContentStatus } from '../../data/portal/schema';

// ── Action → ContentStatus mapping ───────────────────────────────────────────

function actionToStatus(action: VerdictAction): ContentStatus {
  switch (action) {
    case 'APPROVE':               return 'APPROVED';
    case 'APPROVE_WITH_CONDITIONS': return 'APPROVED_WITH_CONDITIONS';
    case 'CHANGE_REQUESTED':      return 'CHANGE_REQUESTED';
    case 'REJECT':                return 'REJECTED';
    case 'WITHDRAW':              return 'WITHDRAWN';
    case 'REAFFIRM':              return 'APPROVED';
    case 'OVERRIDE_SAFETY_CHECK': return 'IN_REVIEW';
    default:                      return 'IN_REVIEW';
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface VerdictSubmitPayload {
  action: VerdictAction;
  /** Required when action is REJECT or CHANGE_REQUESTED (min 10 chars). Optional otherwise. */
  comment: string | null;
  /** Required when item.offLabel or item.controversial is true. Blocks submission otherwise. */
  clinicalJustification: string | null;
}

export interface VerdictSubmitResult {
  /** True while the Firestore write is in flight. */
  submitting: boolean;
  /** Error message if the write failed. null otherwise. */
  error: string | null;
  /** True immediately after submit() is called (optimistic) — reverts if write fails. */
  success: boolean;
  /** Reset state back to initial — call before rendering a new card. */
  reset: () => void;
  submit: (payload: VerdictSubmitPayload) => Promise<void>;
}

// ── SHA-256 helper (Web Crypto API) ───────────────────────────────────────────

/**
 * Recursively sorts object keys alphabetically so JSON.stringify produces
 * a deterministic string regardless of what order Firestore SDKs return keys.
 */
function sortKeysDeep(val: unknown): unknown {
  if (Array.isArray(val)) return val.map(sortKeysDeep);
  if (val !== null && typeof val === 'object') {
    return Object.keys(val as object).sort().reduce<Record<string, unknown>>((acc, k) => {
      acc[k] = sortKeysDeep((val as Record<string, unknown>)[k]);
      return acc;
    }, {});
  }
  return val;
}

/**
 * Compute SHA-256 hex digest of a JSON body object.
 * Keys are sorted recursively before serialization so the hash is stable
 * regardless of Firestore SDK key ordering (gRPC Admin vs REST web SDK).
 */
async function computeBodyHash(body: unknown): Promise<string> {
  const encoded = new TextEncoder().encode(JSON.stringify(sortKeysDeep(body)));
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Manages verdict submission state for a single ClinicalReviewItem.
 *
 * @param tenantId  Firestore tenant ID (e.g. "hemologix")
 * @param contentId Firestore document ID of the ClinicalReviewItem
 * @param item      The full ClinicalReviewItem being reviewed
 */
export function useVerdictSubmit(
  tenantId: string,
  contentId: string,
  item: ClinicalReviewItem,
): VerdictSubmitResult {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const reset = useCallback(() => {
    setSubmitting(false);
    setError(null);
    setSuccess(false);
  }, []);

  const submit = useCallback(
    async (payload: VerdictSubmitPayload): Promise<void> => {
      setSubmitting(true);
      setError(null);

      // ── Pre-flight checks ────────────────────────────────────────────────
      if (!isAuthenticated()) {
        setError('Not authenticated. Please sign in again.');
        setSubmitting(false);
        return;
      }
      const sessionId = getSessionId() ?? 'unknown-session';

      // Validate required fields before network call
      if (
        (item.offLabel || item.controversial) &&
        (!payload.clinicalJustification || payload.clinicalJustification.trim().length === 0)
      ) {
        setError('Clinical justification is required for off-label or controversial items.');
        setSubmitting(false);
        return;
      }

      if (
        (payload.action === 'REJECT' || payload.action === 'CHANGE_REQUESTED') &&
        (!payload.comment || payload.comment.trim().length < 10)
      ) {
        setError('A reason of at least 10 characters is required when rejecting or requesting a change.');
        setSubmitting(false);
        return;
      }

      // ── Compute content hash at verdict time (forensic chain) ────────────
      let contentHashAtReview: string;
      try {
        contentHashAtReview = await computeBodyHash(item.body);
      } catch {
        setError('Failed to compute content hash. Please reload and try again.');
        setSubmitting(false);
        return;
      }

      // ── Session context ──────────────────────────────────────────────────
      const ipAddress = await getSessionIpAddress(sessionId);

      // ── Atomic batch: write verdict + update parent status ───────────────
      const verdictsRef = collection(db, 'tenants', tenantId, 'content', contentId, 'verdicts');
      const newVerdictRef = doc(verdictsRef);
      const contentDocRef = doc(db, 'tenants', tenantId, 'content', contentId);

      // Capture verdictId before batch — needed for offline queue dedup even if commit fails
      const verdictId = newVerdictRef.id;

      const batch = writeBatch(db);

      batch.set(newVerdictRef, {
        action: payload.action,
        actor: sessionId,
        actorEmail: 'portal-reviewer',
        actorDisplayName: 'Portal Reviewer',
        timestamp: serverTimestamp(),
        contentVersion: item.version,
        contentHash: contentHashAtReview,
        comment: payload.comment?.trim() || null,
        clinicalJustification: payload.clinicalJustification?.trim() || null,
        // IP captured server-side at capturePortalLoginSession (D2 auth) and read from
        // portal_sessions/{sessionId} — never empty string in the audit record.
        ipAddress,
        userAgent: navigator.userAgent,
        sessionId,
        previousVerdictHash: null,
      });

      // Update parent ClinicalReviewItem status so real-time listener reflects verdict
      const statusUpdate: Record<string, unknown> = {
        status: actionToStatus(payload.action),
      };
      if (payload.action === 'APPROVE' || payload.action === 'REAFFIRM') {
        statusUpdate['locked'] = true;
        statusUpdate['lockedAt'] = serverTimestamp();
      }
      batch.update(contentDocRef, statusUpdate);

      // ── Optimistic UI: set success immediately, revert only if both online AND offline fail ─
      setSuccess(true);

      try {
        await batch.commit();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown Firestore error';

        // Offline recovery: enqueue to IndexedDB so verdict is not lost.
        // serverTimestamp() is a Firestore sentinel — not serializable to IDB.
        // Use client-side Timestamp as a close approximation for the queued record.
        try {
          await enqueue({
            verdictId,
            tenantId,
            contentId,
            verdictData: {
              action: payload.action,
              actor: sessionId,
              actorEmail: 'portal-reviewer',
              actorDisplayName: 'Portal Reviewer',
              timestamp: Timestamp.fromDate(new Date()),
              contentVersion: item.version,
              contentHash: contentHashAtReview,
              comment: payload.comment?.trim() || null,
              clinicalJustification: payload.clinicalJustification?.trim() || null,
              ipAddress,
              userAgent: navigator.userAgent,
              sessionId,
              previousVerdictHash: null,
              _queuedOffline: true,
            },
          });
          // Notify useOfflineQueue hook to refresh displayed queue depth
          window.dispatchEvent(new CustomEvent('hlx-queue-updated'));
          // Verdict is safe in IndexedDB — optimistic success stands, OfflineIndicator shows count
        } catch {
          // IDB also failed — revert optimistic success and surface error
          setSuccess(false);
          setError(`Verdict write failed: ${msg}. Your verdict was not saved.`);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [tenantId, contentId, item],
  );

  return { submitting, error, success, reset, submit };
}

// ── Content hash TOCTOU check (exported for ReviewCard use) ──────────────────

/**
 * Recomputes the SHA-256 of item.body and compares to item.contentHash.
 * Returns true if content matches staging hash (no tampering detected).
 * Returns false if mismatch — card should show error banner and disable all buttons.
 */
export async function verifyContentHash(item: ClinicalReviewItem): Promise<boolean> {
  try {
    const current = await computeBodyHash(item.body);
    return current === item.contentHash;
  } catch {
    // If we can't compute, treat as mismatch to be safe
    return false;
  }
}
